import type { CsvSubmittedTaskRow, ParseSubmittedTaskCsvResult } from "../types";

const isLineBreak = (char: string): boolean => char === "\n" || char === "\r";

const trimBom = (input: string): string => {
  if (input.length && input.charCodeAt(0) === 0xfeff) {
    return input.slice(1);
  }
  return input;
};

const parseCsvMatrix = (content: string): string[][] => {
  const rows: string[][] = [];
  let currentField = "";
  let currentRow: string[] = [];
  let inQuotes = false;
  let prevCharWasCR = false;

  const pushField = () => {
    currentRow.push(currentField);
    currentField = "";
  };

  const pushRow = () => {
    if (currentRow.length || rows.length === 0) {
      rows.push(currentRow);
    }
    currentRow = [];
  };

  const input = trimBom(content);

  for (let i = 0; i < input.length; i += 1) {
    const char = input[i];
    if (inQuotes) {
      if (char === '"') {
        const nextChar = input[i + 1];
        if (nextChar === '"') {
          currentField += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        currentField += char;
      }
      prevCharWasCR = false;
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      prevCharWasCR = false;
      continue;
    }

    if (char === ",") {
      pushField();
      prevCharWasCR = false;
      continue;
    }

    if (isLineBreak(char)) {
      if (char === "\r") {
        prevCharWasCR = true;
      } else if (char === "\n" && prevCharWasCR) {
        prevCharWasCR = false;
      }
      pushField();
      pushRow();
      currentField = "";
      continue;
    }

    if (prevCharWasCR) {
      pushField();
      pushRow();
      currentField = "";
      prevCharWasCR = false;
    }

    currentField += char;
  }

  pushField();
  if (currentRow.length || currentField.length) {
    pushRow();
  }

  return rows;
};

const sanitizeRows = (matrix: string[][]): string[][] => {
  const result: string[][] = [];
  for (const row of matrix) {
    if (!row.length) continue;
    if (row.length === 1 && !row[0].trim()) {
      continue;
    }
    result.push(row);
  }
  return result;
};

const detectColumnIndex = (header: string[], candidates: string[], fallback = 0): number => {
  const normalized = header.map((cell) => cell.trim().toLowerCase());
  for (const candidate of candidates) {
    const index = normalized.indexOf(candidate);
    if (index >= 0) {
      return index;
    }
  }
  return fallback;
};

export const parseSubmittedTaskCsvContent = (content: string): ParseSubmittedTaskCsvResult => {
  const warnings: string[] = [];
  const errors: string[] = [];
  const matrix = sanitizeRows(parseCsvMatrix(content));

  if (!matrix.length) {
    return { rows: [], warnings: [], errors: [] };
  }

  let headerRow: string[] | null = null;
  let dataStartRow = 0;

  if (matrix.length > 0) {
    const [firstRow] = matrix;
    const normalized = firstRow.map((cell) => cell.trim().toLowerCase());
    const hasTaskIdHeader = normalized.some((cell) => cell === "task_id" || cell === "taskid");
    if (hasTaskIdHeader) {
      headerRow = firstRow;
      dataStartRow = 1;
    }
  }

  const promptIndex = headerRow
    ? detectColumnIndex(headerRow, ["prompt", "prompts"], 0)
    : 0;
  const modelIndex = headerRow
    ? detectColumnIndex(headerRow, ["model_key", "model", "modelkey"], 1)
    : 1;
  const taskIdIndex = headerRow
    ? detectColumnIndex(headerRow, ["task_id", "taskid", "id"], 2)
    : 2;
  const csvRowIndex = headerRow
    ? detectColumnIndex(headerRow, ["csv_row", "csvrow", "csv_index"], -1)
    : -1;

  const rows: CsvSubmittedTaskRow[] = [];
  let dataIndex = 0;

  for (let rowIndex = dataStartRow; rowIndex < matrix.length; rowIndex += 1) {
    const row = matrix[rowIndex];
    const lineNumber = rowIndex + 1;
    const taskId = (row[taskIdIndex] ?? "").trim();
    if (!taskId) {
      warnings.push(`第 ${lineNumber} 行缺少任务 ID，已跳过。`);
      continue;
    }
    dataIndex += 1;
    const prompt = (row[promptIndex] ?? "").trim();
    const modelKey = (row[modelIndex] ?? "").trim();
    const csvRowValue = csvRowIndex >= 0 ? (row[csvRowIndex] ?? "").trim() : "";
    rows.push({
      prompt,
      modelKey,
      taskId,
      csvRow: csvRowValue || undefined,
      rawFields: [...row],
      lineNumber,
      dataIndex,
    });
  }

  if (!rows.length && !warnings.length) {
    errors.push("未从 CSV 中解析到任何任务记录。");
  }

  return { rows, warnings, errors };
};

export default parseSubmittedTaskCsvContent;
